'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { Ref } from 'react';

import styles from './menubar.module.scss';

const HOVER_CLOSE_DELAY_MS = 200;

interface MenuBarContextValue {
  activeId: string | null;
  /** The id of the link/trigger matching the current route — drives the
   *  resting-state indicator when no menu is open. */
  restingActiveId: string | null;
  /** Trigger currently hovered/focused — drives the base underline for items
   *  that don't open a panel (e.g. plain links). */
  hoveredId: string | null;
  setHovered: (id: string | null) => void;
  setRestingActive: (id: string, isActive: boolean) => void;
  openMenu: (id: string, mode: 'pointer' | 'keyboard') => void;
  toggleMenu: (id: string) => void;
  closeMenu: () => void;
  scheduleClose: () => void;
  cancelClose: () => void;
  registerTrigger: (id: string, el: HTMLElement | null) => void;
  isKeyboardOpen: boolean;
  /** Bar element ref — used by MenuBarIndicator to compute relative geometry. */
  barRef: React.RefObject<HTMLElement | null>;
  /** Stable ref to the live trigger map, read by MenuBarIndicator. */
  triggersRef: React.MutableRefObject<Map<string, HTMLElement>>;
}

const MenuBarContext = createContext<MenuBarContextValue | null>(null);

export function useMenuBar(): MenuBarContextValue {
  const ctx = useContext(MenuBarContext);
  if (!ctx) {
    throw new Error('useMenuBar must be used inside <MenuBarProvider>');
  }
  return ctx;
}

interface MenuBarProviderProps {
  children: React.ReactNode;
}

/**
 * Coordinates a row of header menus so that at most one is open at a time.
 * Hovering a new trigger atomically swaps the active menu without re-running
 * close→open transitions. Owns a single hover-close timer for the whole bar.
 *
 * Also exposes barRef + a live trigger map so <MenuBarIndicator> can render
 * an accent underline + bridge caret aligned to the active trigger.
 */
export function MenuBarProvider({ children }: MenuBarProviderProps) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [restingActiveId, setRestingActiveId] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);

  const setHovered = useCallback((id: string | null) => setHoveredId(id), []);

  const closeTimer = useRef<number | null>(null);
  const triggersRef = useRef(new Map<string, HTMLElement>());
  const barRef = useRef<HTMLElement | null>(null);

  const setRestingActive = useCallback((id: string, isActive: boolean) => {
    setRestingActiveId((current) => {
      if (isActive) return id;
      return current === id ? null : current;
    });
  }, []);

  const cancelClose = useCallback(() => {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const closeMenu = useCallback(() => {
    cancelClose();
    setActiveId(null);
    setIsKeyboardOpen(false);
  }, [cancelClose]);

  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimer.current = window.setTimeout(() => {
      setActiveId(null);
      setIsKeyboardOpen(false);
    }, HOVER_CLOSE_DELAY_MS) as unknown as number;
  }, [cancelClose]);

  const openMenu = useCallback(
    (id: string, mode: 'pointer' | 'keyboard') => {
      cancelClose();
      setActiveId(id);
      setIsKeyboardOpen(mode === 'keyboard');
    },
    [cancelClose],
  );

  const toggleMenu = useCallback(
    (id: string) => {
      cancelClose();
      setActiveId((current) => (current === id ? null : id));
      setIsKeyboardOpen(false);
    },
    [cancelClose],
  );

  const registerTrigger = useCallback((id: string, el: HTMLElement | null) => {
    if (el) {
      triggersRef.current.set(id, el);
    } else {
      triggersRef.current.delete(id);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (closeTimer.current !== null) {
        window.clearTimeout(closeTimer.current);
        closeTimer.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (activeId === null) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        const focusTarget = triggersRef.current.get(activeId);
        setActiveId(null);
        setIsKeyboardOpen(false);
        focusTarget?.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [activeId]);

  const value = useMemo<MenuBarContextValue>(
    () => ({
      activeId,
      restingActiveId,
      hoveredId,
      setHovered,
      setRestingActive,
      openMenu,
      toggleMenu,
      closeMenu,
      scheduleClose,
      cancelClose,
      registerTrigger,
      isKeyboardOpen,
      barRef,
      triggersRef,
    }),
    [
      activeId,
      restingActiveId,
      hoveredId,
      setHovered,
      setRestingActive,
      openMenu,
      toggleMenu,
      closeMenu,
      scheduleClose,
      cancelClose,
      registerTrigger,
      isKeyboardOpen,
    ],
  );

  return <MenuBarContext.Provider value={value}>{children}</MenuBarContext.Provider>;
}

/**
 * Sits inside the header bar. Reads the active trigger from MenuBarContext
 * and renders an accent underline pinned to the header's bottom edge plus
 * a small caret-bridge that visually joins the underline to the panel below.
 */
export function MenuBarIndicator() {
  const { activeId, restingActiveId, hoveredId, barRef, triggersRef } =
    useMenuBar();
  const indicatorRef = useRef<HTMLDivElement | null>(null);
  const caretRef = useRef<HTMLDivElement | null>(null);

  // Open menu wins, then the hovered/focused item, then the active route.
  // Hover makes the base stripe follow the pointer across every item,
  // including plain links that never open a panel.
  const targetId = activeId ?? hoveredId ?? restingActiveId;
  const showCaret = activeId !== null;

  useLayoutEffect(() => {
    const indicator = indicatorRef.current;
    const caret = caretRef.current;
    // barRef is populated by the <header> ref callback, which can commit after
    // this layout effect on the first paint. Fall back to a DOM query so the
    // indicator never silently no-ops when the ref hasn't latched yet.
    const bar =
      barRef.current ??
      (typeof document !== 'undefined'
        ? document.querySelector<HTMLElement>('[data-header-bar]')
        : null);
    if (!indicator || !caret || !bar) return;

    if (!targetId) {
      indicator.style.opacity = '0';
      caret.style.opacity = '0';
      return;
    }

    const trigger = triggersRef.current.get(targetId);
    if (!trigger) {
      indicator.style.opacity = '0';
      caret.style.opacity = '0';
      return;
    }

    const update = () => {
      const triggerRect = trigger.getBoundingClientRect();
      const barRect = bar.getBoundingClientRect();

      const left = triggerRect.left - barRect.left;
      const width = triggerRect.width;

      indicator.style.opacity = '1';
      indicator.style.transform = `translateX(${left}px)`;
      indicator.style.width = `${width}px`;

      if (showCaret) {
        const caretWidth = Math.min(width * 0.6, 56);
        const caretLeft = left + (width - caretWidth) / 2;
        caret.style.opacity = '1';
        caret.style.transform = `translateX(${caretLeft}px)`;
        caret.style.width = `${caretWidth}px`;
      } else {
        caret.style.opacity = '0';
      }
    };

    update();
    const onResize = () => update();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, { passive: true });
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize);
    };
  }, [targetId, showCaret, barRef, triggersRef]);

  return (
    <>
      <div ref={indicatorRef} className={styles.indicator} aria-hidden />
      <div ref={caretRef} className={styles.caret} aria-hidden />
    </>
  );
}

/**
 * Registers an element as a menu-bar target so the indicator can pin to it.
 * Reports active-route state so the indicator can rest on the active link
 * when no menu is open. Returns a ref callback that accepts any element
 * type (since the controller only needs getBoundingClientRect on it).
 */
export function useMenuBarRegistration<T extends HTMLElement = HTMLElement>(
  id: string,
  isActive: boolean,
): Ref<T> {
  const { registerTrigger, setRestingActive, setHovered } = useMenuBar();

  useEffect(() => {
    setRestingActive(id, isActive);
    return () => setRestingActive(id, false);
  }, [id, isActive, setRestingActive]);

  const cleanupRef = useRef<(() => void) | null>(null);

  return useCallback(
    (el: T | null) => {
      cleanupRef.current?.();
      cleanupRef.current = null;
      registerTrigger(id, el);

      if (el) {
        const enter = () => setHovered(id);
        const leave = () => setHovered(null);
        el.addEventListener('mouseenter', enter);
        el.addEventListener('mouseleave', leave);
        el.addEventListener('focus', enter);
        el.addEventListener('blur', leave);
        cleanupRef.current = () => {
          el.removeEventListener('mouseenter', enter);
          el.removeEventListener('mouseleave', leave);
          el.removeEventListener('focus', enter);
          el.removeEventListener('blur', leave);
        };
      }
    },
    [id, registerTrigger, setHovered],
  );
}
